import { useState, Fragment, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide';

// MUI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// common
import { AppState } from '@redux/store';
import { IRouteObject } from 'common/types';
import useGeneralHook from 'common/hook/useGeneralHook';
import useCurrentPlanningPeriod from 'common/hook/useCurrentPlanningPeriod';
import Icon from 'common/components/Icon';
import MyLink from 'common/components/MyLink';
import { getBrand } from 'config/brand';
import { SHELL } from 'config/setupTheme';
import { LogoImage } from 'svg';
import { setOpenAsideBar, setOpenAsideBarMobile } from '@redux/cacheReducer';

// screen
import DefaultAsideItems from 'layout/main/DefaultAsideItems';
import ProfileBox from 'layout/main/ProfileBox';
import { ASIDE_WIDTH } from 'layout/main/constants';
import 'react-perfect-scrollbar/dist/css/styles.css';

const HEADER_HEIGHT = 0;
interface Props {
  listRouter: IRouteObject[];
}

const DefaultAside = ({ listRouter }: Props) => {
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [sidebarWidth, setSidebarWidth] = useState(ASIDE_WIDTH);

  const { pathname } = useLocation();
  const { schoolYearLabel } = useCurrentPlanningPeriod();
  const brandName = getBrand().app_name || 'GANET';
  const yearRange = schoolYearLabel?.replace(/\s*-\s*/, '-');
  const { isMobile, isTablet, dispatch } = useGeneralHook();
  const theme = useTheme();
  const sidebarBg = theme.palette.mode === 'dark' ? SHELL.sidebarDark : SHELL.sidebar;
  const openAsideBarMobile = useSelector((state: AppState) => state.cache.openAsideBarMobile);
  const openAsideBar = useSelector((state: AppState) => state.cache.openAsideBar);
  const ASIDE_MIN_WIDTH = isMobile ? 0 : 64;
  const openMerge = isMobile ? openAsideBarMobile : openAsideBar;
  const openAsideTmp = isMobile ? openAsideBarMobile : openAsideBar;

  const mappedRoutes = (list: IRouteObject[], parent?: IRouteObject): IRouteObject[] => {
    return list
      .filter((one) => !one.hidden)
      .map((one) => {
        return {
          ...one,
          path: one.index
            ? parent?.path
            : parent?.path !== '/' && parent?.path
              ? `${parent?.path}/${one.path}`
              : one.path,
          children: one.children && mappedRoutes(one.children, one),
        };
      });
  };

  const listRouterMapped = mappedRoutes(listRouter, { path: '/' });

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    if (sidebarWidth < ASIDE_WIDTH && sidebarWidth > ASIDE_WIDTH / 2) {
      setSidebarWidth(ASIDE_WIDTH);
    } else if (sidebarWidth < ASIDE_WIDTH / 2) {
      dispatch(setOpenAsideBar(false));
      setSidebarWidth(ASIDE_WIDTH);
    }
  }, [dispatch, sidebarWidth]);

  const resize = useCallback(
    (mouseMoveEvent) => {
      if (isResizing) {
        setSidebarWidth(mouseMoveEvent.clientX);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isMobile) {
      dispatch(setOpenAsideBarMobile(false));
    }
  }, [dispatch, isMobile, pathname]);

  // Tablet: sidebar 240px ăn gần 1/3 màn hình -> thu về thanh icon, mở ra xong chuyển trang thì thu lại.
  useEffect(() => {
    if (isTablet) {
      dispatch(setOpenAsideBar(false));
    }
  }, [dispatch, isTablet, pathname]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return (
    <>
      <Box
        id="leftPanel"
        sx={{
          minWidth: openAsideTmp ? sidebarWidth : ASIDE_MIN_WIDTH,
          transition: '0.3s',
        }}
      />

      <Box
        id="leftPanel"
        sx={(theme) => ({
          width: openMerge ? sidebarWidth : ASIDE_MIN_WIDTH,
          height: '100vh',
          transition: isResizing ? 'unset' : 'width 0.3s',
          flexShrink: 0,
          zIndex: 200,
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: HEADER_HEIGHT,
          left: 0,
          bottom: 0,
          willChange: 'width',
          background: sidebarBg,
          color: SHELL.sidebarText,
          boxShadow: '12px 0 40px rgba(7, 20, 34, 0.12)',
          '&:hover .collapse-btn': { opacity: 1 },
          [theme.breakpoints.down('laptop')]: { '& .collapse-btn': { opacity: 1 } },
        })}
        onMouseDown={(e) => e.preventDefault()}
      >
        {openAsideTmp && (
          <Box
            sx={{
              position: 'absolute',
              right: -4,
              top: 0,
              bottom: 0,
              width: 8,
              zIndex: 2,
              cursor: 'col-resize',
              resize: 'horizontal',
              ':hover': {
                '&>div': { opacity: 1 },
              },
            }}
            onMouseDown={startResizing}
          >
            <Box
              sx={{
                bgcolor: 'primary.main',
                opacity: 0,
                height: '100%',
                left: 4,
                position: 'absolute',
                transition: 'opacity 200ms ease 0s',
                width: 2,
              }}
            />
          </Box>
        )}
        <Tooltip title={<FormattedMessage id={openMerge ? 'collapse' : 'expand'} />} arrow>
          <ButtonBase
            className="collapse-btn"
            aria-label={openMerge ? 'Thu gọn menu' : 'Mở rộng menu'}
            sx={(theme) => ({
              borderRadius: '50%',
              position: 'absolute',
              top: 72,
              right: -14,
              minWidth: 28,
              width: 28,
              height: 28,
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 4,
              border: 1,
              borderColor: 'divider',
              zIndex: 3,
              transition: 'all 0.3s',
              opacity: !openAsideTmp ? 1 : 0,
              display: isMobile ? 'none' : undefined,
              ':hover': {
                bgcolor: 'primary.main',
                color: 'common.white',
              },
              [theme.breakpoints.down('laptop')]: { opacity: 1 },
            })}
            onClick={() => {
              dispatch(setOpenAsideBar());
            }}
          >
            <Icon icon={openAsideTmp ? ChevronLeft : ChevronRight} size={14} />
          </ButtonBase>
        </Tooltip>
        <Box
          overflow="hidden"
          borderRight={1}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderColor: 'transparent',
            borderRight: 0,
          }}
        >
          <MyLink to="/" aria-label="Link to HomePage">
            <Box
              sx={{
                mx: openAsideTmp ? 2 : 1,
                mt: 2,
                mb: 1.5,
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: openAsideTmp ? 'flex-start' : 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                  p: '5px',
                }}
              >
                <LogoImage alt={brandName} style={{ width: '100%', height: '100%' }} />
              </Box>
              {openAsideTmp && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: SHELL.sidebarText,
                      fontWeight: 800,
                      fontSize: 26,
                      letterSpacing: '0.06em',
                      lineHeight: 1.25,
                    }}
                  >
                    {brandName}
                  </Typography>
                  {yearRange && (
                    <Typography
                      noWrap
                      sx={{
                        mt: 0.25,
                        color: SHELL.sidebarMuted,
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        lineHeight: 1.2,
                      }}
                    >
                      {yearRange}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </MyLink>
          <PerfectScrollbar style={{ flex: 1 }}>
            <Box
              sx={{
                pb: 2,
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                '& .aside-bar-title': {
                  opacity: openAsideTmp ? 1 : 0,
                  height: openAsideTmp ? 'auto' : 0,
                  padding: openAsideTmp ? undefined : 0,
                  margin: openAsideTmp ? undefined : 0,
                  // Khi mở: để dấu tiếng Việt nhô ra khỏi dòng, không bị overflow cắt.
                  overflow: openAsideTmp ? 'visible' : 'hidden',
                  transition: '0.3s opacity',
                },
              }}
            >
              {listRouterMapped?.map((v: IRouteObject, index: number) => (
                <Fragment key={index}>
                  <Box sx={{ px: isMobile ? 0.5 : 1, pt: index === 0 ? 0.5 : 2, pb: 0.5 }}>
                    <DefaultAsideItems data={v} pathname={pathname} open={openMerge} />
                  </Box>

                </Fragment>
              ))}
            </Box>
          </PerfectScrollbar>
          {isMobile && (
            <Box
              sx={{
                pb: 4,
                width: '100%',
                display: 'flex',
              }}
            >
              <ProfileBox sx={{ width: '100%' }} />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DefaultAside;
